
import React from 'react';
import { PMICalculationResults } from '../types';
import { InfoTooltip } from './InfoTooltip';

interface PMIResultsDisplayProps {
  results: PMICalculationResults | null;
  isLoading: boolean;
}

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
};

export const PMIResultsDisplay: React.FC<PMIResultsDisplayProps> = ({ results, isLoading }) => {
  if (isLoading) {
    return (
      <div className="text-center p-8">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-bp-dark-blue dark:border-bp-light-blue mx-auto"></div>
        <p className="mt-4 text-lg font-semibold text-gray-700 dark:text-gray-300">Calculating the cliff...</p>
      </div>
    );
  }

  if (!results) return null;

  const { baseline, sprint, payoff } = results.strategies;

  // Determine Highest
  let highestNetWorth = baseline;
  if (sprint.finalNetWorth > highestNetWorth.finalNetWorth) highestNetWorth = sprint;
  if (payoff.finalNetWorth > highestNetWorth.finalNetWorth) highestNetWorth = payoff;

  const isSprintHighest = highestNetWorth.strategyName === "Sprint to Cliff";

  const renderStrategyCard = (strategy: typeof baseline, title: string, description: string, color: string) => {
      const isHighest = highestNetWorth.strategyName === strategy.strategyName;
      const borderClass = isHighest 
        ? 'border-4 border-green-500 shadow-xl transform scale-105' 
        : 'border border-gray-200 dark:border-slate-700';
      
      return (
        <div className={`bg-white dark:bg-slate-700 rounded-xl p-4 flex flex-col justify-between ${borderClass} transition-all`}>
            <div>
                {isHighest && <div className="text-xs font-bold uppercase tracking-wide text-green-600 dark:text-green-400 mb-1">Highest Projected Value</div>}
                <h3 className={`font-bold text-lg ${color}`}>{title}</h3>
                <p className="text-sm text-gray-600 dark:text-gray-300 mb-4 h-10">{description}</p>
            </div>
            
            <div className="space-y-2 text-sm border-t dark:border-slate-600 pt-3">
                 <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">PMI Gone In:</span>
                    <span className="font-semibold dark:text-white">{(strategy.monthsUntilPMIRemoved / 12).toFixed(1)} Years</span>
                </div>
                <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Total PMI Cost:</span>
                    <span className="font-semibold dark:text-white">{formatCurrency(strategy.totalPMIPaid)}</span>
                </div>
                <div className="flex justify-between items-center bg-gray-50 dark:bg-slate-800 p-2 rounded">
                    <span className="font-bold text-gray-700 dark:text-gray-200">Final Net Worth:</span>
                    <span className={`font-bold ${isHighest ? 'text-green-600 dark:text-green-400' : 'text-gray-800 dark:text-white'}`}>
                        {formatCurrency(strategy.finalNetWorth)}
                    </span>
                </div>
            </div>
        </div>
      );
  };

  return (
    <div className="mt-8 space-y-6">
        <div className="bg-white dark:bg-slate-800 p-6 rounded-xl shadow-md border border-gray-200 dark:border-slate-700 text-center">
            <h2 className="text-2xl font-bold text-bp-slate dark:text-white mb-2">Projections</h2>
            <p className="text-lg text-gray-700 dark:text-gray-300">
                {isSprintHighest ? (
                    <>
                        <strong className="text-green-600 dark:text-green-400">Sprint to the Cliff</strong> yields the highest net worth. 
                        This models applying extra cash to the mortgage ONLY until PMI is removed, then switching to investing.
                    </>
                ) : highestNetWorth.strategyName === "Full Payoff" ? (
                    <>
                        <strong className="text-blue-600 dark:text-blue-400">Pay it all off.</strong> The mathematical return of eliminating the debt and PMI exceeds the expected investment returns entered.
                    </>
                ) : (
                    <>
                        <strong className="text-purple-600 dark:text-purple-400">Invest the cash.</strong> The cost of PMI is mathematically outweighed by the expected investment returns entered.
                    </>
                )}
            </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {renderStrategyCard(
                baseline, 
                "1. Invest Now", 
                "Pay only the minimums. Invest your extra cash immediately.",
                "text-purple-600 dark:text-purple-400"
            )}
            {renderStrategyCard(
                sprint, 
                "2. The 'Sprint'", 
                "Pay extra to mortgage until PMI is gone. Then invest the extra cash AND the saved PMI.",
                "text-green-600 dark:text-green-400"
            )}
            {renderStrategyCard(
                payoff, 
                "3. Full Payoff", 
                "Aggressively pay down the mortgage until it reaches zero.",
                "text-blue-600 dark:text-blue-400"
            )}
        </div>
        
        {highestNetWorth.strategyName === "Sprint to Cliff" && (
             <div className="bg-green-50 dark:bg-green-900/30 p-4 rounded-lg border border-green-200 dark:border-green-800 flex items-start">
                 <div className="mr-3 mt-1 text-green-600 dark:text-green-400">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                    </svg>
                 </div>
                 <div>
                     <h4 className="font-bold text-green-800 dark:text-green-200">The Math Behind the 'Sprint'</h4>
                     <p className="text-sm text-green-700 dark:text-green-300 mt-1">
                         PMI acts as an unamortized fee. By modeling a sprint, this fee is eliminated {(baseline.monthsUntilPMIRemoved/12 - sprint.monthsUntilPMIRemoved/12).toFixed(1)} years earlier than the baseline, reducing total PMI paid by {formatCurrency(baseline.totalPMIPaid - sprint.totalPMIPaid)}. After the cliff, the model assumes cash flows are redirected to investments.
                     </p>
                 </div>
             </div>
        )}
    </div>
  );
};
